import { SAlert, SBox, SDialog, SLoader, SRow, STableContainer } from "@avalara/skylab-sdk/react";
import React from "react";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { EcmsCertJob, EcmsCertJurisdiction, EcmsRecord } from "../../app/ecmsSlice";
import {
    DetailPair,
    DetailSection,
    displayValue,
    EM_DASH,
    formatDate,
    StatItem,
    StatusTag,
} from "./ecmsDetailHelpers";

/**
 * Read-only "Certificate details" view for an ECMS row, opened from the Ecms Cert ID cell.
 *
 * The dialog seeds its header / summary / static sections from the already-loaded grid
 * {@link EcmsRecord} (instant paint), while the per-jurisdiction ({@link EcmsCertJurisdiction})
 * and job ({@link EcmsCertJob}) tables come from the fetched {@link detail} record — the list
 * call returns those nested arrays as null, so they must be fetched with `$include`.
 * Presentation helpers are shared with EcmsCustomerDetailDialog via ./ecmsDetailHelpers.
 */
interface EcmsCertificateDetailDialogProps {
    /** Grid row that seeds the header + summary; the component renders nothing when null. */
    record: EcmsRecord | null;
    /** Fully-fetched record whose nested detail arrays populate the tables; null until loaded. */
    detail: EcmsRecord | null;
    loading: boolean;
    error: string | null;
    open: boolean;
    onClose: () => void;
}

/** ECMS returns job rows under `exemptCertJobDetails`. */
const jobRows = (detail: EcmsRecord | null): EcmsCertJob[] => detail?.exemptCertJobDetails ?? [];

const jurisdictionRows = (detail: EcmsRecord | null): EcmsCertJurisdiction[] =>
    detail?.exemptCertDetails ?? [];

/** A stable key for a jurisdiction row (region + exemption number, falling back to index). */
const jurisdictionKey = (row: EcmsCertJurisdiction, index: number): string =>
    `${row.region}-${row.idNo ?? ""}-${index}`;

const jobKey = (row: EcmsCertJob, index: number): string =>
    `${row.jobCode ?? ""}-${row.phaseCode ?? ""}-${row.taskCode ?? ""}-${index}`;

/**
 * Jurisdiction (exposure-zone) table — the must-have fields. Effective date is a certificate-level
 * value (ECMS `effDate`), shown on every row since ECMS has no per-jurisdiction effective date.
 */
const JurisdictionTable: React.FC<{
    rows: EcmsCertJurisdiction[];
    effectiveDate?: string;
}> = ({ rows, effectiveDate }) => (
    <STableContainer className="zebra-striped">
        <table>
            <thead>
                <tr>
                    <th>Issuing region</th>
                    <th>Exemption no</th>
                    <th>Exemption no type</th>
                    <th>Effective date</th>
                    <th>End date</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    <tr key={jurisdictionKey(row, index)}>
                        <td>{displayValue(row.region)}</td>
                        <td>{displayValue(row.idNo)}</td>
                        <td>{displayValue(row.idType)}</td>
                        <td>{formatDate(effectiveDate, EM_DASH)}</td>
                        <td>{formatDate(row.endDate, EM_DASH)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </STableContainer>
);

/** Job-phase-task table — rendered only when the certificate carries job details. */
const JobsTable: React.FC<{ rows: EcmsCertJob[] }> = ({ rows }) => (
    <STableContainer className="zebra-striped">
        <table>
            <thead>
                <tr>
                    <th>Job code</th>
                    <th>Phase code</th>
                    <th>Task code</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    <tr key={jobKey(row, index)}>
                        <td>{displayValue(row.jobCode)}</td>
                        <td>{displayValue(row.phaseCode)}</td>
                        <td>{displayValue(row.taskCode)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </STableContainer>
);

const EcmsCertificateDetailDialog: React.FC<EcmsCertificateDetailDialogProps> = ({
    record,
    detail,
    loading,
    error,
    open,
    onClose,
}) => {
    // No seed row means nothing to show — render nothing rather than an empty dialog shell.
    if (!record) {
        return null;
    }

    const jurisdictions = jurisdictionRows(detail);
    const jobs = jobRows(detail);
    // Certificate-level effective date. ECMS returns it as `effDate`; the legacy `effectiveDate`
    // alias is left unset, so prefer effDate and fall back for older payloads.
    const effectiveDate =
        detail?.effDate ?? record.effDate ?? detail?.effectiveDate ?? record.effectiveDate;

    return (
        <SDialog
            open={open}
            id="ecms-certificate-detail-dialog"
            style={{ width: "52vw" }}
            aria-modal="true"
            onS-dismiss={onClose}
            data-testid={testIDs.ecms.certificateDetailDialog}>
            <div slot="header" className="flex flex-dir-col align-items-start text-left">
                <h2 className="margin-none">Certificate details</h2>
                <span className="text-sm text-muted margin-top-xs">
                    ECMS Cert ID {displayValue(record.exemptCertId)}
                </span>
            </div>
            <div slot="body">
                <SBox className="secondary margin-bottom-md">
                    <SRow>
                        <StatItem label="Certificate type">
                            {displayValue(record.exemptCertTypeId)}
                        </StatItem>
                        <StatItem label="Expires">
                            {formatDate(record.expiryDate, EM_DASH)}
                        </StatItem>
                        <StatItem label="Status">
                            <StatusTag value={record.exemptCertStatusId} />
                        </StatItem>
                    </SRow>
                </SBox>

                <DetailSection title="Certificate information">
                    <DetailPair label="Cert ID" value={displayValue(record.avaCertId)} />
                    <DetailPair
                        label="Review status"
                        value={displayValue(record.exemptCertReviewStatusId)}
                    />
                    <DetailPair
                        label="Issuing country"
                        value={displayValue(record.countryIssued)}
                    />
                    <DetailPair
                        label="Effective date"
                        value={formatDate(effectiveDate, "Not set")}
                    />
                    <DetailPair
                        label="Regions applicable"
                        value={displayValue(record.regionsApplicable)}
                    />
                    <DetailPair
                        label="Document ref no"
                        value={displayValue(record.documentRefNo)}
                    />
                </DetailSection>

                <DetailSection title="Exemption reason" withDivider>
                    <DetailPair
                        label="Exemption reason"
                        value={displayValue(record.exemptReasonDescription)}
                    />
                    <DetailPair
                        label="Business type"
                        value={displayValue(record.businessTypeDescription)}
                    />
                </DetailSection>

                <section>
                    <hr />
                    <h3 className="text-md font-semibold margin-top-none margin-bottom-sm">
                        Jurisdiction details
                    </h3>
                    {loading ? (
                        <SLoader loading aria-live="polite" />
                    ) : error ? (
                        <SAlert status="error" noDismiss>
                            {error}
                        </SAlert>
                    ) : jurisdictions.length > 0 ? (
                        <JurisdictionTable rows={jurisdictions} effectiveDate={effectiveDate} />
                    ) : (
                        <p className="text-sm text-muted">No jurisdiction details.</p>
                    )}
                </section>

                {!loading && !error && jobs.length > 0 ? (
                    <section>
                        <hr />
                        <h3 className="text-md font-semibold margin-top-none margin-bottom-sm">
                            Jobs
                        </h3>
                        <JobsTable rows={jobs} />
                    </section>
                ) : null}
            </div>
            <div slot="footer">
                <button type="button" className="primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </SDialog>
    );
};

export default EcmsCertificateDetailDialog;
